import { IconButton } from '@mui/material';
import { XIcon } from '@phosphor-icons/react';
import {
  BaseEdge,
  EdgeLabelRenderer,
  type EdgeProps,
  Position,
  getSmoothStepPath,
} from '@xyflow/react';

import { colors, isTruthy } from 'common';

import { useReactFlowViewContext } from '../context/context';

export function NotableEdge(props: EdgeProps) {
  const { id, style, sourceX, sourceY, targetX, targetY, data, selected } = props;
  const { reactFlowMutators, isEditable } = useReactFlowViewContext();

  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    borderRadius: 16,
  });
  const isNodeHovered = isTruthy(data?.isNodeHovered);
  const isEdgeHovered = isTruthy(data?.isEdgeHovered);
  const isEdgeHighlighted = isNodeHovered || isEdgeHovered || selected;
  const isDeleteButtonVisible = isEdgeHovered || selected;

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        style={{
          stroke: isEdgeHighlighted ? colors.extended.blue.primary : '#BFBFBF',
          strokeWidth: 2,
          strokeDasharray: isEdgeHighlighted ? '4' : undefined,
          transition: 'ease-in-out 0.2s',
          transitionProperty: 'stroke-width, stroke-dasharray',
          ...style,
        }}
      />
      {isEditable && (
        <EdgeLabelRenderer>
          <IconButton
            size="small"
            aria-label={`Delete Edge ${id}`}
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              opacity: isDeleteButtonVisible ? 1 : 0,
              backgroundColor: isDeleteButtonVisible
                ? colors.danger.light
                : colors.background.default,
              color: colors.danger.dark,
              pointerEvents: 'all',
              transition: 'ease-in-out 0.2s',
              transitionProperty: 'opacity, color, background-color',
              zIndex: isEdgeHighlighted
                ? /**
                   * The default react-flow zIndex for edges is 1000.
                   * If the edge is selected, this needs to be at the same level or higher
                   * to ensure the delete button is clickable.
                   * Note also - zIndex does not work if opacity is set to 0.
                   * See also stacking context and its relation to zIndex:
                   * https://codepen.io/jazeee/pen/vjZLNN
                   * Also, be aware that the user experience related to when this button is visible is dependent
                   * on these choices, and it isn't necessarily intuitive. See:
                   * https://www.loom.com/share/257acf0a887145f5804a566e25f72e2e
                   */
                  1000
                : undefined,
            }}
            className="nodrag nopan"
            onClick={() => {
              if (isEditable) {
                reactFlowMutators?.deleteEdge(id);
              }
            }}
          >
            <XIcon />
          </IconButton>
        </EdgeLabelRenderer>
      )}
    </>
  );
}
